import { createSlice, PayloadAction, SerializedError } from '@reduxjs/toolkit'
import {
  createTwilioToken,
  login,
  logout,
  restoreSession,
  verifyToken,
} from '../actions/auth.action' // Import the actions from auth.action.ts
import { trimProviderId } from '@services/api'
import {
  clearAccessToken,
  getAccessToken,
  setAccessToken,
} from '@services/auth/accessTokenStore'
import {
  isKioskDeviceMode,
  isStandalonePatientFormsPath,
} from '@utils/standalonePatientForms.util'

function shouldVerifyOnBoot(): boolean {
  if (typeof window === 'undefined') return false
  if (isKioskDeviceMode()) return false
  return !isStandalonePatientFormsPath(window.location.pathname)
}

// Define the initial state type
type AuthState = {
  accessToken: string | null
  isAuthenticated: boolean
  /** True while verifyToken/restoreSession is in flight (e.g. on refresh). Used to avoid redirecting to login before verification completes. */
  isVerifyingToken: boolean
  twilioToken: string | null
  loading: boolean
  error: string | null | SerializedError // Update error type to handle various types of errors
  tenantId: string | null
  dbName: string | null
}

// Initial state for auth
// Note: isAuthenticated starts as false - it will be set to true only after token verification
// Access token lives in memory only; boot restore uses HttpOnly refresh cookie.
const initialState: AuthState = {
  accessToken: getAccessToken(),
  isAuthenticated: false, // Don't auto-authenticate - require token verification
  // Always verify/restore on boot for app routes so ProtectedRoute does not flash to /login
  isVerifyingToken: shouldVerifyOnBoot(),
  twilioToken: localStorage.getItem('twilio_token'),
  loading: false,
  error: null,
  tenantId: localStorage.getItem('tenant_id'),
  dbName: localStorage.getItem('db_name'),
}

// Create the auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Login cases
    builder.addCase(login.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(
      login.fulfilled,
      (
        state,
        action: PayloadAction<{
          access_token: string
          refresh_token?: string
          login_type: 'user' | 'staff'
          user_id: string
          username: string
          user_role?: string
          user_scope: string[] | string
          tenant_id: string
          db_name: string
          provider_id?: string
        }>,
      ) => {
        const {
          access_token,
          login_type,
          user_id,
          username,
          user_role,
          user_scope,
          tenant_id,
          db_name,
        } = action.payload

        const actorId = user_id ?? ''
        const providerId = trimProviderId(action.payload.provider_id)

        // Access token in memory only (refresh token is HttpOnly cookie from auth-service)
        setAccessToken(access_token)
        localStorage.setItem(
          'user',
          JSON.stringify({
            user_id: actorId,
            login_type,
            username,
            user_role,
            user_scope,
            ...(providerId ? { provider_id: providerId } : {}),
          }),
        )
        localStorage.setItem('tenant_id', tenant_id)
        localStorage.setItem('db_name', db_name)

        // Update state
        state.accessToken = access_token
        state.tenantId = tenant_id
        state.dbName = db_name
        state.isAuthenticated = true
        state.isVerifyingToken = false
        state.loading = false
        state.error = null
      },
    )
    builder.addCase(login.rejected, (state, action) => {
      state.loading = false
      state.error = action.error // action.error will contain SerializedError
      state.isAuthenticated = false
      state.isVerifyingToken = false
    })

    // Logout cases
    builder.addCase(
      logout.fulfilled,
      (state, action: PayloadAction<boolean>) => {
        clearAccessToken()
        state.accessToken = null
        state.tenantId = null
        state.dbName = null
        state.isAuthenticated = false
        state.isVerifyingToken = false
        state.loading = false
        state.error = null

        if (action.payload) {
          state.error = 'Session expired. Please login again.'
        }
      },
    )
    builder.addCase(logout.rejected, (state) => {
      clearAccessToken()
      state.accessToken = null
      state.tenantId = null
      state.dbName = null
      state.isAuthenticated = false
      state.isVerifyingToken = false
      state.loading = false
    })

    builder
      .addCase(createTwilioToken.rejected, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createTwilioToken.pending, (state) => {
        state.loading = false
        state.twilioToken = null
      })
      .addCase(createTwilioToken.fulfilled, (state, action) => {
        state.loading = false
        state.twilioToken = action.payload.token
        localStorage.setItem('twilio_token', action.payload.token)
      })

    // Restore session (cookie refresh + verify) on page load
    builder.addCase(restoreSession.pending, (state) => {
      state.loading = true
      state.isVerifyingToken = true
      state.error = null
    })
    builder.addCase(restoreSession.fulfilled, (state, action) => {
      state.accessToken = getAccessToken()
      state.isAuthenticated = true
      state.loading = false
      state.isVerifyingToken = false
      state.error = null
      if (action.payload.tenant_id) {
        state.tenantId = action.payload.tenant_id
        localStorage.setItem('tenant_id', action.payload.tenant_id)
      }
      if (action.payload.db_name) {
        state.dbName = action.payload.db_name
        localStorage.setItem('db_name', action.payload.db_name)
      }
      const providerId = trimProviderId(action.payload.provider_id)
      const existing = JSON.parse(localStorage.getItem('user') ?? '{}')
      const scpRaw =
        typeof action.payload.scp === 'string' ? action.payload.scp.trim() : ''
      const userScopeFromToken =
        scpRaw.length > 0 ? scpRaw.split(/\s+/).filter(Boolean) : null
      const nextUser = {
        ...existing,
        ...(action.payload.sub_typ
          ? { login_type: action.payload.sub_typ }
          : {}),
        ...(providerId ? { provider_id: providerId } : {}),
        ...(userScopeFromToken != null
          ? { user_scope: userScopeFromToken }
          : {}),
      }
      if (action.payload.sub_typ || providerId || userScopeFromToken != null) {
        localStorage.setItem('user', JSON.stringify(nextUser))
      }
    })
    builder.addCase(restoreSession.rejected, (state) => {
      clearAccessToken()
      state.accessToken = null
      state.isAuthenticated = false
      state.loading = false
      state.isVerifyingToken = false
    })

    // Verify token cases
    builder.addCase(verifyToken.pending, (state) => {
      state.loading = true
      state.isVerifyingToken = true
      state.error = null
    })
    builder.addCase(
      verifyToken.fulfilled,
      (
        state,
        action: PayloadAction<{
          sub: string
          username: string
          sub_typ?: 'user' | 'staff'
          user_role?: string
          user_scope?: string
          scp?: string
          tenant_id: string
          db_name: string
          provider_id?: string
        }>,
      ) => {
        // Token is valid, user is authenticated
        state.accessToken = getAccessToken()
        state.isAuthenticated = true
        state.loading = false
        state.isVerifyingToken = false
        state.error = null
        // Update tenant_id and db_name from verified token if they exist
        if (action.payload.tenant_id) {
          state.tenantId = action.payload.tenant_id
          localStorage.setItem('tenant_id', action.payload.tenant_id)
        }
        if (action.payload.db_name) {
          state.dbName = action.payload.db_name
          localStorage.setItem('db_name', action.payload.db_name)
        }
        const providerId = trimProviderId(action.payload.provider_id)
        const existing = JSON.parse(localStorage.getItem('user') ?? '{}')
        const scpRaw =
          typeof action.payload.scp === 'string'
            ? action.payload.scp.trim()
            : ''
        const userScopeFromToken =
          scpRaw.length > 0 ? scpRaw.split(/\s+/).filter(Boolean) : null
        const nextUser = {
          ...existing,
          ...(action.payload.username
            ? { username: action.payload.username }
            : {}),
          ...(action.payload.sub_typ
            ? { login_type: action.payload.sub_typ }
            : {}),
          ...(providerId ? { provider_id: providerId } : {}),
          ...(userScopeFromToken != null
            ? { user_scope: userScopeFromToken }
            : {}),
        }
        if (
          action.payload.username ||
          action.payload.sub_typ ||
          providerId ||
          userScopeFromToken != null
        ) {
          localStorage.setItem('user', JSON.stringify(nextUser))
        }
      },
    )
    builder.addCase(verifyToken.rejected, (state) => {
      // Token verification failed - clear auth state (do not wipe all localStorage)
      clearAccessToken()
      state.accessToken = null
      state.tenantId = null
      state.dbName = null
      state.isAuthenticated = false
      state.loading = false
      state.isVerifyingToken = false
      // Don't set error here - it's expected if token doesn't exist or is invalid
    })
  },
})

// Export the reducer
export default authSlice.reducer
